/* ══════════════════════════════════════════════════════════
   Store Locator — Frontend logic
   ══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var state = {
    root: null,
    proxyUrl: '',
    storeKey: '',
    settings: null,
    contractors: [],
    filtered: [],
    activeId: null,
    hoverId: null,
    map: null,
    markers: {},
    markerCluster: null,
    search: '',
    specialtyFilter: '',
    popupCloseTimer: null,
    // ZIP / proximity search:
    // When the user types a ZIP/postal code into the search box, we resolve
    // it to lat/lng via the proxy and store the result here. Filters then
    // compute Haversine distance from this point to each contractor and
    // sort the list nearest-first.
    userLocation: null,        // { lat, lng, zip, displayName } or null
    geocodingZip: false,       // request in flight
    lastGeocodedZip: '',       // dedupe / avoid re-fetching same ZIP
    userLocationMarker: null,  // Leaflet marker for "you are here"
    // Project gallery lightbox (lazy-created on first use, portaled to body)
    lightboxEl: null,
    lightboxImages: [],
    lightboxIndex: 0,
    lightboxPrevBodyOverflow: '',
  };

  // Fallback placeholder logo when a contractor has no logo_url.
  // This is the same "worker.png" generic icon the legacy Excel data
  // used. Hosted on the Torchstar Shopify CDN — fast, cached globally.
  var PLACEHOLDER_LOGO_URL =
    'https://cdn.shopify.com/s/files/1/0663/7030/6266/files/worker.png?v=1744659632';

  // Single source of truth for which logo to render. Returns the
  // contractor's own logo if set, otherwise the shared placeholder.
  function getLogoUrl(c) {
    if (c && typeof c.logo_url === 'string' && c.logo_url.trim()) {
      return c.logo_url;
    }
    return PLACEHOLDER_LOGO_URL;
  }

  var el = {};

  function init() {
    state.root = document.getElementById('storeLocatorApp');
    if (!state.root) return;

    state.proxyUrl = state.root.getAttribute('data-proxy-url') || 'https://crm-relay.torchstar.us';
    state.storeKey = state.root.getAttribute('data-store-key') || 'torchstar';

    el.title = state.root.querySelector('[data-sl-title]');
    el.subtitle = state.root.querySelector('[data-sl-subtitle]');
    el.count = state.root.querySelector('[data-sl-count]');
    el.searchInput = state.root.querySelector('[data-sl-search]');
    el.specialtySelect = state.root.querySelector('[data-sl-specialty]');
    el.list = state.root.querySelector('[data-sl-list]');
    el.mapEl = state.root.querySelector('[data-sl-map]');
    el.modal = state.root.querySelector('[data-sl-modal]');
    el.modalBody = state.root.querySelector('[data-sl-modal-body]');
    el.sidebar = state.root.querySelector('[data-sl-sidebar]');
    el.mapContainer = state.root.querySelector('.sl-map-col');
    el.filterBar = state.root.querySelector('[data-sl-filter-bar]');

    // Portal modal to <body> so it escapes any parent stacking context.
    // Shopify section wrappers may apply transform/filter/will-change to their
    // own containers, which would trap our position:fixed modal inside the
    // section's stacking context (and let later sections render on top of it).
    // Moving the modal to <body> guarantees it's positioned relative to the
    // viewport itself, with z-index 9999 always winning.
    if (el.modal && el.modal.parentNode !== document.body) {
      // Tag it so we can restyle/find it later if needed
      el.modal.setAttribute('data-sl-portaled', 'true');
      document.body.appendChild(el.modal);
    }

    bindEvents();
    loadAll();
  }

  // ── Data loading ──────────────────────────────────────
  async function loadAll() {
    try {
      var results = await Promise.allSettled([
        fetchJSON('/api/locator/' + state.storeKey + '/settings'),
        fetchJSON('/api/locator/' + state.storeKey + '/contractors'),
      ]);

      if (results[0].status === 'fulfilled') {
        state.settings = results[0].value;
        applySettings(state.settings);
      } else {
        console.warn('[Store Locator] Settings fetch failed:', results[0].reason);
      }

      if (results[1].status === 'fulfilled') {
        state.contractors = results[1].value.data || [];
        state.filtered = state.contractors.slice();
        populateSpecialtyFilter();
        updateCount();
        renderList();
        initMap();
      } else {
        console.error('[Store Locator] Contractors fetch failed:', results[1].reason);
        showEmpty('Unable to load directory at this time.');
      }
    } catch (err) {
      console.error('[Store Locator] Load error:', err);
      showEmpty();
    }
  }

  function fetchJSON(path) {
    return fetch(state.proxyUrl + path, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // ── Apply CRM-driven settings ────────────────────────
  function applySettings(s) {
    if (!s) return;

    // Targets that need the brand CSS variables.
    // Modal is portaled to <body> at init, so it no longer inherits from
    // the section. We must set the same vars on the modal element directly.
    var targets = [state.root];
    if (el.modal && el.modal.getAttribute('data-sl-portaled') === 'true') {
      targets.push(el.modal);
    }

    function setVar(prop, value) {
      targets.forEach(function (t) { t.style.setProperty(prop, value); });
    }

    if (s.primary_color) setVar('--sl-primary', s.primary_color);
    if (s.secondary_color) setVar('--sl-secondary', s.secondary_color);
    if (s.accent_color) setVar('--sl-accent', s.accent_color);
    if (s.text_color) setVar('--sl-ink', s.text_color);
    if (s.background_color) setVar('--sl-bg', s.background_color);
    if (s.marker_color) setVar('--sl-marker', s.marker_color);
    if (s.marker_active_color) setVar('--sl-marker-active', s.marker_active_color);

    // Derive several primary-related CSS vars from a single primary_color:
    //   --sl-primary-tint  : 8% alpha (active card background)
    //   --sl-primary-hover : ~22% darker (gradient endpoint, button hover, etc.)
    // This way the user only sets ONE color in the CRM and the entire color
    // family stays cohesive (gradient placeholder, hover states, etc.)
    if (s.primary_color) {
      var hex = s.primary_color.replace('#', '');
      if (hex.length === 6) {
        var r = parseInt(hex.substring(0, 2), 16);
        var g = parseInt(hex.substring(2, 4), 16);
        var b = parseInt(hex.substring(4, 6), 16);
        setVar('--sl-primary-tint', 'rgba(' + r + ',' + g + ',' + b + ',0.08)');
        // Multiply each channel by 0.78 = ~22% darker
        setVar('--sl-primary-hover',
          'rgb(' + Math.round(r * 0.78) + ',' + Math.round(g * 0.78) + ',' + Math.round(b * 0.78) + ')');
      }
    }

    if (s.page_title && el.title) el.title.textContent = s.page_title;
    if (s.page_subtitle && el.subtitle) el.subtitle.textContent = s.page_subtitle;

    if (s.show_search_box === false && el.filterBar) el.filterBar.style.display = 'none';
    if (s.show_list_panel === false && el.sidebar) el.sidebar.style.display = 'none';

    // Hide specialty filter if disabled in CRM
    if (s.enable_specialties_filter === false) {
      var filterWrap = state.root.querySelector('.sl-toolbar__filter');
      if (filterWrap) filterWrap.style.display = 'none';
    }
  }

  function updateCount() {
    if (!el.count) return;
    var n = state.filtered.length;
    if (n === 0) {
      el.count.textContent = 'No results';
    } else {
      el.count.textContent = (n === 1 ? '1 Contractor' : n + ' Contractors');
    }
  }

  // ── Specialty filter dropdown ────────────────────────
  function populateSpecialtyFilter() {
    if (!el.specialtySelect) return;
    var allSpecialties = new Set();
    state.contractors.forEach(function (c) {
      (c.specialties || []).forEach(function (s) { allSpecialties.add(s); });
    });
    var sorted = Array.from(allSpecialties).sort();
    el.specialtySelect.innerHTML = '<option value="">All specialties</option>' +
      sorted.map(function (s) {
        return '<option value="' + escapeAttr(s) + '">' + escapeHtml(capitalize(s)) + '</option>';
      }).join('');
  }

  // ── ZIP / proximity helpers ──────────────────────────
  // Match common postal code shapes — primarily US 5-digit (and ZIP+4)
  // but also tolerant of Canadian (A1A 1A1) and UK formats.
  function looksLikeZip(query) {
    var q = query.trim();
    if (!q) return false;
    // US 5-digit, optionally + 4
    if (/^\d{5}(-\d{4})?$/.test(q)) return true;
    // Canadian postcode (A1A 1A1, A1A1A1)
    if (/^[A-Z]\d[A-Z][\s-]?\d[A-Z]\d$/i.test(q)) return true;
    // UK rough match
    if (/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(q)) return true;
    return false;
  }

  // Haversine distance in miles between two lat/lng pairs.
  function haversineMiles(lat1, lng1, lat2, lng2) {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
    var R = 3958.7613; // Earth radius in miles
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function formatDistance(mi) {
    if (mi == null || !isFinite(mi)) return '';
    if (mi < 0.1) return '< 0.1 mi';
    if (mi < 10) return mi.toFixed(1) + ' mi';
    return Math.round(mi) + ' mi';
  }

  // Async: resolve a ZIP to lat/lng via the proxy. Stores result on
  // state.userLocation, then re-applies filters so the list resorts.
  function geocodeZipAndApply(zip) {
    if (!zip || zip === state.lastGeocodedZip) return;
    state.lastGeocodedZip = zip;
    state.geocodingZip = true;
    fetch(state.proxyUrl + '/api/locator/geocode-zip?zip=' + encodeURIComponent(zip), {
      credentials: 'omit',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (data && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          state.userLocation = {
            lat: data.latitude,
            lng: data.longitude,
            zip: zip,
            displayName: data.display_name || zip,
          };
          // Drop a "you are here" marker on the map
          showUserLocationMarker(state.userLocation);
          // Re-filter (will now sort by distance + show distance badges)
          applyFilters();
          // Pan map to the user's location at a reasonable city zoom
          if (state.map) {
            state.map.flyTo([state.userLocation.lat, state.userLocation.lng], 11, { duration: 0.7 });
          }
        }
      })
      .catch(function (err) {
        console.warn('[ZIP geocode] failed:', err);
        // Soft-fail: leave userLocation null, list shows in default order
      })
      .finally(function () {
        state.geocodingZip = false;
      });
  }

  function clearUserLocation() {
    state.userLocation = null;
    state.lastGeocodedZip = '';
    if (state.userLocationMarker && state.map) {
      try { state.map.removeLayer(state.userLocationMarker); } catch (e) { /* noop */ }
    }
    state.userLocationMarker = null;
  }

  // Drop a distinctive "you are here" marker for the searched ZIP
  function showUserLocationMarker(loc) {
    if (!state.map || typeof L === 'undefined' || !loc) return;
    if (state.userLocationMarker) {
      try { state.map.removeLayer(state.userLocationMarker); } catch (e) { /* noop */ }
    }
    var icon = L.divIcon({
      className: 'sl-user-location-marker',
      html: '<div class="sl-user-pin"><div class="sl-user-pin__pulse"></div><div class="sl-user-pin__dot"></div></div>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });
    state.userLocationMarker = L.marker([loc.lat, loc.lng], { icon: icon, zIndexOffset: 1000 });
    state.userLocationMarker.bindTooltip('Searched: ' + (loc.zip || ''), {
      direction: 'top',
      offset: L.point(0, -8),
      className: 'sl-user-tooltip',
    });
    state.userLocationMarker.addTo(state.map);
  }

  // ── Filtering ────────────────────────────────────────
  function applyFilters() {
    var rawQuery = state.search.trim();
    var q = rawQuery.toLowerCase();
    var specialty = state.specialtyFilter;

    // Detect ZIP query and trigger async geocode (only fires once per ZIP).
    // The geocode result will re-call applyFilters when it lands.
    if (rawQuery && looksLikeZip(rawQuery)) {
      if (rawQuery !== state.lastGeocodedZip) {
        geocodeZipAndApply(rawQuery);
      }
    } else {
      // Query is not a ZIP — drop any previously held user location so the
      // list goes back to default order.
      if (state.userLocation) clearUserLocation();
    }

    var hasUserLoc = !!state.userLocation;

    // Compute distances + filter
    var rows = state.contractors.map(function (c) {
      var dist = hasUserLoc
        ? haversineMiles(state.userLocation.lat, state.userLocation.lng, c.latitude, c.longitude)
        : null;
      return { c: c, _distance: dist };
    });

    rows = rows.filter(function (row) {
      var c = row.c;
      // Specialty filter (always applies)
      if (specialty && (!c.specialties || c.specialties.indexOf(specialty) === -1)) return false;

      // Text query: only apply non-ZIP free-text against name/contact/city/state.
      // ZIP queries are handled by proximity, NOT by text match (since we
      // strip address_zip from the public API for privacy).
      if (q && !hasUserLoc) {
        var hay = [
          c.business_name,
          c.contact_name,
          c.address_city,
          c.address_state,
        ].filter(Boolean).join(' ').toLowerCase();
        if (hay.indexOf(q) === -1) return false;
      }

      return true;
    });

    // If we have a user location, sort by distance ascending.
    if (hasUserLoc) {
      rows.sort(function (a, b) { return a._distance - b._distance; });
    }

    // Stamp distance back onto the contractor copy used by the renderer
    state.filtered = rows.map(function (row) {
      // Don't mutate the original contractor — make a thin copy
      var copy = {};
      for (var k in row.c) { if (Object.prototype.hasOwnProperty.call(row.c, k)) copy[k] = row.c[k]; }
      copy._distanceMi = row._distance;
      return copy;
    });

    updateCount();
    renderList();
    updateMarkers();
  }

  // ── Render list ──────────────────────────────────────
  function renderList() {
    if (!el.list) return;

    if (state.filtered.length === 0) {
      showEmpty();
      return;
    }

    var html = state.filtered.map(function (c, idx) {
      return renderListItem(c, idx);
    }).join('');
    el.list.innerHTML = html;

    // Bind card interactions
    el.list.querySelectorAll('[data-sl-item]').forEach(function (node) {
      var id = node.getAttribute('data-sl-item');

      // Click card → flyTo + highlight (does NOT open modal)
      node.addEventListener('click', function (e) {
        // Ignore clicks on the CTA button (handled separately below)
        if (e.target.closest('[data-sl-view-details]')) return;
        onCardSelect(id);
      });

      // Hover card → highlight corresponding marker (no flyTo)
      node.addEventListener('mouseenter', function () {
        setHoverMarker(id);
      });
      node.addEventListener('mouseleave', function () {
        setHoverMarker(null);
      });
    });

    // Bind "View Details" buttons — only these open the modal
    el.list.querySelectorAll('[data-sl-view-details]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        var id = btn.getAttribute('data-sl-view-details');
        var c = state.contractors.find(function (x) { return String(x.id) === String(id); });
        if (c) openDetail(c);
      });
    });
  }

  function renderListItem(c, idx) {
    var location = [c.address_city, c.address_state].filter(Boolean).join(', ') || '—';
    var activeClass = c.id === state.activeId ? ' is-active' : '';

    var logoHtml =
      '<img class="sl-list-item__logo" src="' + escapeAttr(getLogoUrl(c)) +
      '" alt="' + escapeAttr(c.business_name) + '" loading="lazy" />';

    var specialtyTags = '';
    if (c.specialties && c.specialties.length) {
      specialtyTags = '<div class="sl-list-item__specialties">' +
        c.specialties.slice(0, 3).map(function (s) {
          return '<span class="sl-specialty-tag">' + escapeHtml(capitalize(s)) + '</span>';
        }).join('') + '</div>';
    }

    var ctaLabel = (state.settings && state.settings.view_details_label) || 'View Details';

    // Distance badge if we have a user-searched location
    var distanceBadge = '';
    if (typeof c._distanceMi === 'number' && isFinite(c._distanceMi)) {
      distanceBadge =
        '<span class="sl-list-item__distance">' +
          '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
            '<path d="M2 12h6M16 12h6M12 2v6M12 16v6"/><circle cx="12" cy="12" r="2"/>' +
          '</svg>' +
          escapeHtml(formatDistance(c._distanceMi)) +
        '</span>';
    }

    return (
      '<article class="sl-list-item' + activeClass + '" data-sl-item="' + escapeAttr(c.id) + '">' +
        '<div class="sl-list-item__top">' +
          logoHtml +
          '<div class="sl-list-item__main">' +
            '<div class="sl-list-item__name-row">' +
              '<h3 class="sl-list-item__name">' + escapeHtml(c.business_name) + '</h3>' +
              distanceBadge +
            '</div>' +
            '<p class="sl-list-item__location">' +
              '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
                '<path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0116 0z"/>' +
                '<circle cx="12" cy="10" r="3"/>' +
              '</svg>' +
              escapeHtml(location) +
            '</p>' +
            specialtyTags +
          '</div>' +
        '</div>' +
        '<button type="button" class="sl-list-item__cta" data-sl-view-details="' + escapeAttr(c.id) + '" aria-label="View details">' +
          '<span>' + escapeHtml(ctaLabel) + '</span>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12" aria-hidden="true">' +
            '<path d="M5 12h14M12 5l7 7-7 7"/>' +
          '</svg>' +
        '</button>' +
      '</article>'
    );
  }

  function showEmpty(message) {
    message = message || (state.settings && state.settings.empty_state_text) || 'No contractors match your search.';
    el.list.innerHTML =
      '<div class="sl-empty">' +
        '<svg class="sl-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
          '<circle cx="11" cy="11" r="8"/>' +
          '<line x1="21" y1="21" x2="16.65" y2="16.65"/>' +
        '</svg>' +
        '<p class="sl-empty__text">' + escapeHtml(message) + '</p>' +
      '</div>';
  }

  // ── Card / marker selection ─────────────────────────
  // Click card → fly map to marker + highlight both (NO modal)
  function onCardSelect(id) {
    var c = state.contractors.find(function (x) { return String(x.id) === String(id); });
    if (!c) return;

    state.activeId = id;
    renderList();

    if (state.map && c.latitude != null && c.longitude != null) {
      state.map.flyTo([c.latitude, c.longitude], Math.max(state.map.getZoom(), 12), { duration: 0.7 });
      // Open the marker popup after flyTo settles
      var marker = state.markers[id];
      if (marker) {
        state.map.once('moveend', function () {
          try { marker.openPopup(); } catch (e) { /* ignore */ }
        });
      }
    }

    updateMarkerStates();
  }

  // Hover card → just highlight marker, no flyTo, no popup
  function setHoverMarker(id) {
    if (state.hoverId === id) return;
    state.hoverId = id;
    updateMarkerStates();
  }

  // Scroll a list card into view (triggered by marker hover/click)
  function scrollCardIntoView(id) {
    if (!el.list) return;
    var card = el.list.querySelector('[data-sl-item="' + cssEscape(id) + '"]');
    if (card && card.scrollIntoView) {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function cssEscape(val) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(val);
    return String(val).replace(/["\\]/g, '\\$&');
  }

  // ── Map ──────────────────────────────────────────────
  function initMap() {
    if (!el.mapEl || typeof L === 'undefined') return;

    var s = state.settings || {};
    var centerLat = s.default_center_lat != null ? parseFloat(s.default_center_lat) : 39.8283;
    var centerLng = s.default_center_lng != null ? parseFloat(s.default_center_lng) : -98.5795;
    var zoom = s.default_zoom || 4;

    state.map = L.map(el.mapEl, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      // markercluster requires a map maxZoom; the MapLibre basemap does not provide one
      maxZoom: 19,
    }).setView([centerLat, centerLng], zoom);

    // Basemap. CARTO raster tiles now require an API key (keyless requests return a
    // watermark image), so light/dark maps use OpenFreeMap vector tiles via MapLibre GL.
    // tile_provider 'osm' keeps OpenStreetMap raster tiles; map_style 'dark' takes precedence.
    if (s.map_style !== 'dark' && s.tile_provider === 'osm') {
      var tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        subdomains: 'abc', // OSM only has a/b/c — NOT d
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
        // Preload neighboring tiles so edges don't flash white
        keepBuffer: 4,
        // Don't thrash tile requests during zoom animation
        updateWhenZooming: false,
        updateWhenIdle: false,
        crossOrigin: true,
      });

      // Retry failed tiles once (handles transient CDN errors)
      tileLayer.on('tileerror', function (e) {
        if (!e.tile || e.tile.dataset.slRetried) return;
        e.tile.dataset.slRetried = '1';
        var src = e.tile.src;
        setTimeout(function () {
          e.tile.src = src + (src.indexOf('?') > -1 ? '&' : '?') + '_r=' + Date.now();
        }, 300);
      });

      tileLayer.addTo(state.map);
    } else {
      L.maplibreGL({
        style: 'https://tiles.openfreemap.org/styles/' + (s.map_style === 'dark' ? 'dark' : 'positron'),
        attribution: '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      }).addTo(state.map);
    }

    // Respect CRM cluster_markers toggle
    var useCluster = s.cluster_markers !== false && typeof L.markerClusterGroup === 'function';
    if (useCluster) {
      state.markerCluster = L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        maxClusterRadius: 50,
      });
      state.map.addLayer(state.markerCluster);
    }

    addAllMarkers();

    // ── Fix white-tile bug ────────────────────────────
    // Leaflet computes tile bounds from container size at init time,
    // but parent flex/grid/image loads can resize the container later,
    // leaving uncovered tiles blank. Force recompute on every change.
    var invalidate = function () {
      if (state.map) state.map.invalidateSize({ animate: false, pan: false });
    };

    // 1. After paint (layout settled)
    requestAnimationFrame(invalidate);
    // 2. After a tick (fonts, CSS)
    setTimeout(invalidate, 200);
    // 3. After all resources loaded (images in page can shift layout)
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { setTimeout(invalidate, 100); }, { once: true });
    }

    // 4. Observe container resize (e.g., sidebar collapsing, responsive breakpoint)
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () { invalidate(); });
      ro.observe(el.mapEl);
      if (el.mapContainer) ro.observe(el.mapContainer);
      state._resizeObserver = ro;
    }

    // 5. Window resize fallback
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(invalidate, 100);
    });
  }

  function addAllMarkers() {
    state.markers = {};
    state.contractors.forEach(function (c) {
      if (c.latitude == null || c.longitude == null) return;
      var icon = L.divIcon({
        className: 'sl-custom-marker',
        html: '<div class="sl-marker-pin"></div>',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      var marker = L.marker([c.latitude, c.longitude], { icon: icon });

      // Bind lightweight hover popup (logo + name + location + View Details button)
      marker.bindPopup(buildMarkerPopupHtml(c), {
        className: 'sl-popup',
        closeButton: false,
        autoClose: false,
        closeOnClick: false,
        autoPan: false,
        offset: L.point(0, -6),
        minWidth: 220,
        maxWidth: 260,
      });

      // Hover marker → open popup + highlight card + scroll it into view
      marker.on('mouseover', function () {
        clearTimeout(state.popupCloseTimer);
        state.hoverId = c.id;
        updateMarkerStates();
        scrollCardIntoView(c.id);
        if (!marker.isPopupOpen()) marker.openPopup();
      });
      marker.on('mouseout', function () {
        state.hoverId = null;
        updateMarkerStates();
        // Delay close so user can move cursor into the popup
        state.popupCloseTimer = setTimeout(function () {
          if (c.id !== state.activeId) marker.closePopup();
        }, 180);
      });

      // Click marker → set active + sync list (same as clicking a card)
      marker.on('click', function () {
        clearTimeout(state.popupCloseTimer);
        state.activeId = c.id;
        renderList();
        updateMarkerStates();
        scrollCardIntoView(c.id);
        if (!marker.isPopupOpen()) marker.openPopup();
      });

      // When popup opens, bind "View Details" button inside it + keep-open on hover
      marker.on('popupopen', function (e) {
        var popupEl = e.popup.getElement();
        if (!popupEl) return;

        // Keep popup open while cursor is over it
        popupEl.addEventListener('mouseenter', function () {
          clearTimeout(state.popupCloseTimer);
        });
        popupEl.addEventListener('mouseleave', function () {
          state.popupCloseTimer = setTimeout(function () {
            if (c.id !== state.activeId) marker.closePopup();
          }, 180);
        });

        // Bind "View Details" button inside popup
        var viewBtn = popupEl.querySelector('[data-sl-popup-view]');
        if (viewBtn) {
          viewBtn.addEventListener('click', function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            openDetail(c);
          });
        }
      });

      state.markers[c.id] = marker;
      if (state.markerCluster) {
        state.markerCluster.addLayer(marker);
      } else {
        marker.addTo(state.map);
      }
    });

    var ids = Object.keys(state.markers);
    if (ids.length > 0 && state.markerCluster) {
      try {
        var bounds = state.markerCluster.getBounds();
        if (bounds.isValid()) {
          state.map.fitBounds(bounds, { padding: [60, 60], maxZoom: 10 });
        }
      } catch (e) { /* ignore */ }
    }
  }

  // Lightweight popup content for hover/click on map marker
  function buildMarkerPopupHtml(c) {
    var location = [c.address_city, c.address_state].filter(Boolean).join(', ');
    var ctaLabel = (state.settings && state.settings.view_details_label) || 'View Details';

    var logoHtml =
      '<img class="sl-popup__logo" src="' + escapeAttr(getLogoUrl(c)) + '" alt="" />';

    return (
      '<div class="sl-popup__body">' +
        '<div class="sl-popup__header">' +
          logoHtml +
          '<div class="sl-popup__title-wrap">' +
            '<h4 class="sl-popup__title">' + escapeHtml(c.business_name) + '</h4>' +
            (location ? '<p class="sl-popup__location">' + escapeHtml(location) + '</p>' : '') +
          '</div>' +
        '</div>' +
        '<button type="button" class="sl-popup__cta" data-sl-popup-view>' +
          '<span>' + escapeHtml(ctaLabel) + '</span>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12" aria-hidden="true">' +
            '<path d="M5 12h14M12 5l7 7-7 7"/>' +
          '</svg>' +
        '</button>' +
      '</div>'
    );
  }

  function updateMarkers() {
    if (!state.markerCluster) return;
    state.markerCluster.clearLayers();
    state.filtered.forEach(function (c) {
      var marker = state.markers[c.id];
      if (marker) state.markerCluster.addLayer(marker);
    });
  }

  function updateMarkerStates() {
    Object.keys(state.markers).forEach(function (id) {
      var marker = state.markers[id];
      var iconEl = marker.getElement && marker.getElement();
      if (!iconEl) return;
      var pin = iconEl.querySelector('.sl-marker-pin');
      if (pin) {
        var isActive = String(id) === String(state.activeId);
        var isHover = String(id) === String(state.hoverId) && !isActive;
        pin.classList.toggle('is-active', isActive);
        pin.classList.toggle('is-hover', isHover);
      }
    });
  }

  // ── Detail modal ─────────────────────────────────────
  async function openDetail(c) {
    if (!el.modal) return;

    var detail = c;
    try {
      var full = await fetchJSON('/api/locator/' + state.storeKey + '/contractors/' + c.id);
      detail = full;
    } catch (e) {
      console.warn('[Store Locator] Detail fetch failed, using list data', e);
    }

    el.modalBody.innerHTML = renderDetail(detail);
    el.modal.classList.add('is-open');
    el.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDetail() {
    if (!el.modal) return;
    el.modal.classList.remove('is-open');
    el.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function renderDetail(c) {
    var coverHtml = c.cover_url
      ? '<div class="sl-detail__cover"><img src="' + escapeAttr(c.cover_url) + '" alt="" /></div>'
      : '<div class="sl-detail__cover sl-detail__cover--placeholder"></div>';

    var logoHtml =
      '<img class="sl-detail__logo" src="' + escapeAttr(getLogoUrl(c)) +
      '" alt="' + escapeAttr(c.business_name) + '" />';

    var location = [c.address_city, c.address_state, c.address_country].filter(Boolean).join(', ');

    var infoItems = [];
    if (location) infoItems.push(svgIcon('map-pin') + '<span>' + escapeHtml(location) + '</span>');
    if (c.phone_public) infoItems.push(svgIcon('phone') + '<a href="tel:' + escapeAttr(c.phone_public) + '">' + escapeHtml(c.phone_public) + '</a>');
    if (c.email_public) infoItems.push(svgIcon('mail') + '<a href="mailto:' + escapeAttr(c.email_public) + '">' + escapeHtml(c.email_public) + '</a>');
    if (c.website) {
      var displayUrl = c.website.replace(/^https?:\/\//, '').replace(/\/$/, '');
      infoItems.push(svgIcon('globe') + '<a href="' + escapeAttr(c.website) + '" target="_blank" rel="noopener">' + escapeHtml(displayUrl) + '</a>');
    }
    if (c.service_radius_miles) infoItems.push(svgIcon('radius') + '<span>Service radius: ' + c.service_radius_miles + ' mi</span>');

    // ── Trust & Verification Badges ──────────────────────
    // Surface Licensed / Insured / Years-in-business as visible trust signals.
    // is_licensed is computed server-side from license_number (raw number is private).
    // insured comes straight from the boolean column.
    var trustBadges = [];
    if (c.is_licensed) {
      trustBadges.push({ label: 'Licensed' });
    }
    if (c.insured) {
      trustBadges.push({ label: 'Insured' });
    }
    if (c.years_in_business) {
      trustBadges.push({ label: c.years_in_business + '+ Years in Business' });
    }

    var trustBadgesHtml = '';
    if (trustBadges.length) {
      trustBadgesHtml =
        '<div class="sl-detail__section">' +
          '<h4 class="sl-detail__section-title">Trust &amp; Verification</h4>' +
          '<div class="sl-detail__trust-badges">' +
            trustBadges.map(function (b) {
              return (
                '<div class="sl-trust-badge">' +
                  '<svg class="sl-trust-badge__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                    '<path d="M9 12l2 2 4-4"/>' +
                    '<circle cx="12" cy="12" r="10"/>' +
                  '</svg>' +
                  '<span>' + escapeHtml(b.label) + '</span>' +
                '</div>'
              );
            }).join('') +
          '</div>' +
        '</div>';
    }

    var specialtyHtml = '';
    if (c.specialties && c.specialties.length) {
      specialtyHtml =
        '<div class="sl-detail__section">' +
          '<h4 class="sl-detail__section-title">Specialties</h4>' +
          '<div class="sl-detail__specialties">' +
            c.specialties.map(function (s) {
              return '<span class="sl-detail__specialty">' + escapeHtml(capitalize(s)) + '</span>';
            }).join('') +
          '</div>' +
        '</div>';
    }

    var projectsHtml = '';
    if (c.projects && c.projects.length && (!state.settings || state.settings.enable_project_gallery !== false)) {
      projectsHtml =
        '<div class="sl-detail__section">' +
          '<h4 class="sl-detail__section-title">Project Gallery</h4>' +
          c.projects.map(function (p) {
            var dateStr = p.completion_date ? formatDate(p.completion_date) : '';
            var cityStr = p.location_city || '';
            var typeStr = p.project_type || '';
            var metaParts = [typeStr, cityStr, dateStr].filter(Boolean);
            var photos = '';
            if (p.photos && p.photos.length) {
              photos = '<div class="sl-detail__gallery">' +
                p.photos.map(function (ph) {
                  return '<img src="' + escapeAttr(ph.url) + '" alt="' + escapeAttr(ph.caption || p.title) + '" loading="lazy" />';
                }).join('') +
              '</div>';
            }
            return (
              '<div class="sl-detail__project">' +
                '<div class="sl-detail__project-header">' +
                  '<h5 class="sl-detail__project-title">' + escapeHtml(p.title) + '</h5>' +
                '</div>' +
                (metaParts.length ? '<p class="sl-detail__project-meta">' + metaParts.join(' · ') + '</p>' : '') +
                (p.description ? '<p class="sl-detail__project-desc">' + escapeHtml(p.description) + '</p>' : '') +
                photos +
              '</div>'
            );
          }).join('') +
        '</div>';
    }

    var ctaLabel = (state.settings && state.settings.contact_cta_label) || 'Request a Consultation';
    var ctaButtons = '';
    if (c.email_public) {
      ctaButtons += '<a class="sl-detail__cta-btn" href="mailto:' + escapeAttr(c.email_public) + '">' +
        svgIcon('mail') + '<span>' + escapeHtml(ctaLabel) + '</span>' +
      '</a>';
    }
    if (c.phone_public) {
      ctaButtons += '<a class="sl-detail__cta-btn sl-detail__cta-btn--outline" href="tel:' + escapeAttr(c.phone_public) + '">' +
        svgIcon('phone') + '<span>Call</span>' +
      '</a>';
    }

    return (
      // Inline style on the SVG forces visibility against host theme global
      // resets like Ella's `svg { fill: currentColor; width: 1rem; height: 1rem }`.
      // Inline style has higher specificity than any stylesheet rule (without
      // !important), so this is the most resilient way to render the close icon.
      '<button type="button" class="sl-modal__close" data-sl-modal-close aria-label="Close" ' +
        'style="color:#0f172a;">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
          'style="display:block;width:16px;height:16px;fill:none;stroke:currentColor;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;">' +
          '<path d="M18 6L6 18M6 6l12 12" style="fill:none;stroke:currentColor;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;"/>' +
        '</svg>' +
      '</button>' +
      coverHtml +
      '<div class="sl-detail__body">' +
        '<div class="sl-detail__header">' +
          logoHtml +
          '<div class="sl-detail__title-wrap">' +
            '<h2 class="sl-detail__title">' + escapeHtml(c.business_name) + '</h2>' +
            (c.contact_name ? '<p class="sl-detail__contact-name">' + escapeHtml(c.contact_name) + '</p>' : '') +
          '</div>' +
        '</div>' +

        (c.bio
          ? '<div class="sl-detail__section"><h4 class="sl-detail__section-title">About</h4><p class="sl-detail__bio">' + escapeHtml(c.bio) + '</p></div>'
          : '') +

        (infoItems.length
          ? '<div class="sl-detail__section"><h4 class="sl-detail__section-title">Contact &amp; Info</h4>' +
            '<div class="sl-detail__info-grid">' +
              infoItems.map(function (i) { return '<div class="sl-detail__info-item">' + i + '</div>'; }).join('') +
            '</div></div>'
          : '') +

        trustBadgesHtml +
        specialtyHtml +
        projectsHtml +

        (ctaButtons ? '<div class="sl-detail__cta">' + ctaButtons + '</div>' : '') +
      '</div>'
    );
  }

  // ── Events ───────────────────────────────────────────
  function bindEvents() {
    if (el.searchInput) {
      var searchTimer;
      el.searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          state.search = el.searchInput.value;
          applyFilters();
        }, 180);
      });
    }

    if (el.specialtySelect) {
      el.specialtySelect.addEventListener('change', function () {
        state.specialtyFilter = el.specialtySelect.value;
        applyFilters();
      });
    }

    state.root.querySelectorAll('[data-sl-view]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = btn.getAttribute('data-sl-view');
        state.root.querySelectorAll('[data-sl-view]').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        if (el.sidebar) el.sidebar.classList.toggle('is-mobile-visible', view === 'list');
        if (el.mapContainer) el.mapContainer.classList.toggle('is-mobile-visible', view === 'map');
        if (view === 'map' && state.map) {
          setTimeout(function () { state.map.invalidateSize(); }, 100);
        }
      });
    });

    // Bind close click directly on the modal element itself (not document).
    // Why: some host themes (Ella) attach document-level click handlers that
    // may call stopPropagation(), preventing our click from bubbling up.
    // By listening on the modal node directly + capture phase, we catch the
    // event before any upstream interference.
    //
    // Logic:
    //   1. If click is on the explicit close button or overlay -> close
    //   2. Otherwise, if click is NOT inside the panel -> close
    //      (covers the case where click lands on the modal's padding area
    //      and the event target is the .sl-modal element itself, not the
    //      overlay nor the panel)
    if (el.modal) {
      el.modal.addEventListener('click', function (e) {
        // Explicit close (X button or overlay element)
        if (e.target.closest('[data-sl-modal-close]')) {
          closeDetail();
          return;
        }
        // Gallery image click → open lightbox with all sibling images
        var galleryImg = e.target.closest('.sl-detail__gallery img');
        if (galleryImg) {
          var gallery = galleryImg.closest('.sl-detail__gallery');
          if (gallery) {
            var allImgs = Array.prototype.slice.call(gallery.querySelectorAll('img'));
            var photos = allImgs.map(function (i) {
              return { url: i.getAttribute('src'), caption: i.getAttribute('alt') || '' };
            });
            openLightbox(photos, allImgs.indexOf(galleryImg));
          }
          return;
        }
        // Anywhere outside the panel content → close detail modal
        if (!e.target.closest('.sl-modal__panel')) {
          closeDetail();
        }
      }, true);
    }

    document.addEventListener('keydown', function (e) {
      // Lightbox takes keyboard priority when open
      if (state.lightboxEl && state.lightboxEl.classList.contains('is-open')) {
        if (e.key === 'Escape') { closeLightbox(); return; }
        if (e.key === 'ArrowLeft') { lightboxPrev(); return; }
        if (e.key === 'ArrowRight') { lightboxNext(); return; }
        return;
      }
      // Detail modal Escape
      if (e.key === 'Escape' && el.modal && el.modal.classList.contains('is-open')) closeDetail();
    });
  }

  // ══════════════════════════════════════════════════════════
  // Project Gallery Lightbox
  // Self-contained, portaled to <body>, keyboard + click nav.
  // ══════════════════════════════════════════════════════════
  function ensureLightbox() {
    if (state.lightboxEl) return state.lightboxEl;
    var wrap = document.createElement('div');
    wrap.className = 'sl-lightbox';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.setAttribute('role', 'dialog');
    wrap.innerHTML =
      '<div class="sl-lightbox__overlay" data-sl-lightbox-close></div>' +
      '<button type="button" class="sl-lightbox__close" data-sl-lightbox-close aria-label="Close image" ' +
        'style="color:#fff;">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
          'style="display:block;width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;">' +
          '<path d="M18 6L6 18M6 6l12 12"/>' +
        '</svg>' +
      '</button>' +
      '<button type="button" class="sl-lightbox__nav sl-lightbox__prev" data-sl-lightbox-prev aria-label="Previous image" ' +
        'style="color:#fff;">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
          'style="display:block;width:28px;height:28px;fill:none;stroke:currentColor;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;">' +
          '<path d="M15 18l-6-6 6-6"/>' +
        '</svg>' +
      '</button>' +
      '<button type="button" class="sl-lightbox__nav sl-lightbox__next" data-sl-lightbox-next aria-label="Next image" ' +
        'style="color:#fff;">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
          'style="display:block;width:28px;height:28px;fill:none;stroke:currentColor;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;">' +
          '<path d="M9 6l6 6-6 6"/>' +
        '</svg>' +
      '</button>' +
      '<figure class="sl-lightbox__figure">' +
        '<img class="sl-lightbox__image" src="" alt="" />' +
        '<figcaption class="sl-lightbox__caption"></figcaption>' +
      '</figure>' +
      '<div class="sl-lightbox__counter"></div>';

    document.body.appendChild(wrap);
    state.lightboxEl = wrap;

    // Delegation listener inside lightbox (capture phase to beat host theme interference)
    wrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-sl-lightbox-close]')) {
        closeLightbox();
      } else if (e.target.closest('[data-sl-lightbox-prev]')) {
        lightboxPrev();
      } else if (e.target.closest('[data-sl-lightbox-next]')) {
        lightboxNext();
      }
    }, true);

    return wrap;
  }

  function openLightbox(images, index) {
    if (!Array.isArray(images) || images.length === 0) return;
    state.lightboxImages = images;
    state.lightboxIndex = Math.max(0, Math.min(index || 0, images.length - 1));
    ensureLightbox();
    updateLightboxUI();
    state.lightboxEl.classList.add('is-open');
    state.lightboxEl.setAttribute('aria-hidden', 'false');
    // Prevent body scroll while lightbox is open
    state.lightboxPrevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!state.lightboxEl) return;
    state.lightboxEl.classList.remove('is-open');
    state.lightboxEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = state.lightboxPrevBodyOverflow || '';
  }

  function lightboxNext() {
    if (!state.lightboxImages || state.lightboxImages.length < 2) return;
    state.lightboxIndex = (state.lightboxIndex + 1) % state.lightboxImages.length;
    updateLightboxUI();
  }

  function lightboxPrev() {
    if (!state.lightboxImages || state.lightboxImages.length < 2) return;
    state.lightboxIndex = (state.lightboxIndex - 1 + state.lightboxImages.length) % state.lightboxImages.length;
    updateLightboxUI();
  }

  function updateLightboxUI() {
    if (!state.lightboxEl) return;
    var img = (state.lightboxImages || [])[state.lightboxIndex] || {};
    var imgEl = state.lightboxEl.querySelector('.sl-lightbox__image');
    var capEl = state.lightboxEl.querySelector('.sl-lightbox__caption');
    var counterEl = state.lightboxEl.querySelector('.sl-lightbox__counter');
    var prevBtn = state.lightboxEl.querySelector('.sl-lightbox__prev');
    var nextBtn = state.lightboxEl.querySelector('.sl-lightbox__next');
    var total = (state.lightboxImages || []).length;

    if (imgEl) {
      // Trigger zoom-in animation on every image change
      imgEl.style.animation = 'none';
      imgEl.offsetHeight; // force reflow
      imgEl.style.animation = '';
      imgEl.src = img.url || '';
      imgEl.alt = img.caption || '';
    }
    if (capEl) {
      capEl.textContent = img.caption || '';
      capEl.style.display = img.caption ? 'block' : 'none';
    }
    if (counterEl) {
      counterEl.textContent = (state.lightboxIndex + 1) + ' / ' + total;
      counterEl.style.display = total > 1 ? 'block' : 'none';
    }
    if (prevBtn) prevBtn.style.display = total > 1 ? 'flex' : 'none';
    if (nextBtn) nextBtn.style.display = total > 1 ? 'flex' : 'none';
  }

  // ── Helpers ──────────────────────────────────────────
  function escapeHtml(str) {
    var d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }
  function escapeAttr(str) {
    return String(str == null ? '' : str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  function formatDate(dateStr) {
    try {
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch (e) { return dateStr; }
  }
  function svgIcon(name) {
    var icons = {
      'map-pin': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0116 0z"/><circle cx="12" cy="10" r="3"/></svg>',
      'phone': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>',
      'mail': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
      'globe': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>',
      'radius': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2" fill="currentColor"/></svg>',
      'award': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="7"/><polyline points="8.21,13.89 7,23 12,20 17,23 15.79,13.88"/></svg>',
    };
    return icons[name] || '';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
